/**
 * Credit Store
 *
 * Global state for credit balance and out-of-credits status.
 * All values are in CREDITS (not millicredits). Conversion happens at boundaries:
 * - Langgraph sends millicredits → converted in updateFromCreditStatus
 * - Rails sends credits via inertia_share → used directly in hydrateFromPageProps
 *
 * Key behaviors:
 * - `justRanOut` triggers the modal to show
 * - Modal is dismissable but won't re-show for 1 hour
 * - `isOutOfCredits` determines if chat inputs should be locked
 */
import { create } from "zustand";
import { subscribeWithSelector, persist } from "zustand/middleware";

export interface CreditState {
  // Balance tracking (in credits, decimals allowed)
  balance: number | null;
  planCredits: number | null;
  packCredits: number | null;
  planCreditsAllocated: number | null; // Total credits allocated per period
  periodEndsAt: string | null; // ISO 8601 date when credits reset

  // Out of credits state
  isOutOfCredits: boolean;

  // Trial status — true when the active subscription is in "trialing" status.
  // Drives out-of-credits modal copy: trial users see "upgrade now" rather
  // than "wait for reset" since trial credits are a one-time grant.
  isTrialing: boolean;

  // ISO 8601 date when the trial ends. Only set when isTrialing is true.
  // Used for "Expires on X" copy (in place of "Resets on X") across the
  // settings page and the credit modal's low-credit variant.
  trialEndsAt: string | null;

  // Data for the "Upgrade Now" confirmation modal. Non-null when:
  //   (a) the user is actively trialing a Stripe subscription (happy path), OR
  //   (b) the trial JUST failed — status is past_due/incomplete AND the webhook
  //       cached a decline reason on the subscription (sad path).
  // The `paymentFailed` flag distinguishes the two so the modal can swap copy
  // and route the primary CTA to the Stripe billing portal instead of the
  // upgrade endpoint.
  trialUpgrade: {
    subscriptionPrefixId: string;
    planDisplayName: string;
    amountCents: number;
    currency: string;
    interval: string;
    paymentFailed: boolean;
    declineMessage: string | null;
    updatePaymentMethodPath: string | null;
  } | null;

  // Modal control
  showOutOfCreditsModal: boolean;
  // Stores the `periodEndsAt` value that was current when the user dismissed
  // the OOC modal. Combined with the live `periodEndsAt`, this gives us two
  // independent re-arm triggers:
  //   1. Credits return mid-period (handled in updateFromCreditStatus /
  //      hydrateFromPageProps) — the field is reset to null.
  //   2. The billing period rolls over (periodEndsAt changes) — the stored
  //      string no longer matches, so the dismissal is implicitly cleared.
  // Persisting the period boundary instead of a boolean prevents the dismissal
  // from sticking across renewals when localStorage outlives the period.
  oocModalDismissedForPeriod: string | null;

  // Low credit warning modal (80% threshold)
  showLowCreditModal: boolean;
  lowCreditWarningDismissedAt: number | null;

  // Timestamp when credits were exhausted (persisted to localStorage).
  // Used to guard against stale Rails data on reload — the async Sidekiq charge
  // may not have completed yet. Expires after EXHAUSTION_GUARD_TTL_MS.
  exhaustedAt: number | null;
}

export interface CreditActions {
  /**
   * Update credit balance from Langgraph stream response.
   * Langgraph sends millicredits, so we convert here.
   * If justExhausted is true, triggers the modal to show (respecting dismiss timeout).
   */
  updateFromCreditStatus: (status: {
    estimatedRemainingMillicredits: number; // Langgraph uses millicredits
    justExhausted: boolean;
  }) => void;

  /**
   * Update balance from a 402 error response.
   * The error includes millicredits, so we convert here.
   */
  updateFromBalanceCheck: (balance: {
    balanceMillicredits: number;
    planMillicredits: number;
    packMillicredits: number;
    isExhausted: boolean;
  }) => void;

  /**
   * Hydrate from Inertia page props.
   * Called in SiteLayout on every navigation to ensure credit state persists.
   * Rails sends credits (already converted), so no conversion needed.
   */
  hydrateFromPageProps: (
    props: {
      plan_credits?: number | null;
      pack_credits?: number | null;
      total_credits?: number | null;
      plan_credits_allocated?: number | null;
      period_ends_at?: string | null;
      trial_ends_at?: string | null;
      is_trialing?: boolean | null;
      // False when the account has never had any subscription. Such accounts
      // start at 0 credits (pre-trial), so we must NOT treat 0 as exhaustion
      // and pop the out-of-credits modal. Undefined on pages that don't send it.
      has_ever_subscribed?: boolean | null;
      trial_upgrade?: {
        subscription_prefix_id: string;
        plan_display_name: string;
        amount_cents: number;
        currency: string;
        interval: string;
        payment_failed?: boolean;
        decline_message?: string | null;
        update_payment_method_path?: string | null;
      } | null;
    } | null
  ) => void;

  /**
   * Close the OOC modal. Marks the current exhausted state as acknowledged
   * so the modal won't re-pop on subsequent navigations until credits are
   * restored and the user runs out again.
   */
  dismissModal: () => void;

  /**
   * Show the modal manually (e.g., when user tries to submit with no credits).
   */
  showModal: () => void;

  /**
   * Dismiss the low credit warning modal.
   * Records the dismiss time to prevent re-showing for 24 hours.
   */
  dismissLowCreditWarning: () => void;

  /**
   * Reset the store (e.g., on logout).
   */
  reset: () => void;
}

export type CreditStore = CreditState & CreditActions;

const LOW_CREDIT_WARNING_SUPPRESS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const LOW_CREDIT_WARNING_THRESHOLD_PERCENT = 80; // Show warning at 80% usage

// How long the persisted exhaustion guard blocks stale Rails hydration.
// After this, Rails is authoritative again (Sidekiq has definitely finished).
const EXHAUSTION_GUARD_TTL_MS = 2 * 60 * 1000; // 2 minutes - just manage a small window where Sidekiq may still be processing

const initialState: CreditState = {
  balance: null,
  planCredits: null,
  packCredits: null,
  planCreditsAllocated: null,
  periodEndsAt: null,
  isOutOfCredits: true, // Default to disabled until hydration confirms credits
  isTrialing: false,
  trialEndsAt: null,
  trialUpgrade: null,
  showOutOfCreditsModal: false,
  oocModalDismissedForPeriod: null,
  showLowCreditModal: false,
  lowCreditWarningDismissedAt: null,
  exhaustedAt: null,
};

/**
 * Convert millicredits to credits.
 * 1000 millicredits = 1 credit
 */
function millicreditsToCredits(millicredits: number): number {
  return Math.round((millicredits / 1000) * 100) / 100; // Round to 2 decimals
}

export const useCreditStore = create<CreditStore>()(
  persist(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      updateFromCreditStatus: (status) => {
        const { estimatedRemainingMillicredits, justExhausted } = status;

        const credits = millicreditsToCredits(estimatedRemainingMillicredits);

        if (justExhausted) {
          // Force-show exhausted modal (overrides any previous dismiss)
          // and close the low credit modal since exhausted takes priority
          set({
            balance: credits,
            isOutOfCredits: true,
            exhaustedAt: Date.now(),
            showOutOfCreditsModal: true,
            oocModalDismissedForPeriod: null,
            showLowCreditModal: false,
          });
        } else {
          const isExhausted = credits <= 0;
          set({
            balance: credits,
            isOutOfCredits: isExhausted,
            exhaustedAt: isExhausted ? Date.now() : null,
            // Credits restored — re-arm the modal so the next exhaustion pops.
            ...(isExhausted ? {} : { oocModalDismissedForPeriod: null }),
          });
        }
      },

      updateFromBalanceCheck: (balance) => {
        set({
          balance: millicreditsToCredits(balance.balanceMillicredits),
          planCredits: millicreditsToCredits(balance.planMillicredits),
          packCredits: millicreditsToCredits(balance.packMillicredits),
          isOutOfCredits: balance.isExhausted,
          exhaustedAt: balance.isExhausted ? Date.now() : null,
        });
      },

      hydrateFromPageProps: (props) => {
        if (!props) return;

        const {
          plan_credits,
          pack_credits,
          total_credits,
          plan_credits_allocated,
          period_ends_at,
          trial_ends_at,
          is_trialing,
          has_ever_subscribed,
          trial_upgrade,
        } = props;

        // Only update if we have actual data
        const updates: Partial<CreditState> = {};

        if (total_credits !== undefined && total_credits !== null) {
          // Guard: if we recently detected exhaustion (via stream or 402) but Rails
          // hasn't caught up yet (async Sidekiq charge), keep the exhausted state.
          // The guard expires after EXHAUSTION_GUARD_TTL_MS so buying credits works.
          const { exhaustedAt } = get();
          const recentlyExhausted =
            exhaustedAt !== null &&
            Date.now() - exhaustedAt < EXHAUSTION_GUARD_TTL_MS;

          if (recentlyExhausted && total_credits > 0) {
            // Stale Rails data — don't override recent exhaustion
          } else {
            updates.balance = total_credits;
            updates.isOutOfCredits = total_credits <= 0;
            if (total_credits > 0) {
              updates.exhaustedAt = null;
              // Credits are back — re-arm the modal so the next exhaustion
              // pops again. This is the edge that resets the dismissal.
              updates.oocModalDismissedForPeriod = null;
            } else {
              // We're OOC. Dismissal is only effective if it was made for the
              // SAME billing period the user is currently in — otherwise the
              // period has rolled over and the modal needs to re-pop with the
              // fresh exhaustion. Period boundary comes from Rails (period_ends_at).
              const dismissedForPeriod = get().oocModalDismissedForPeriod;
              const livePeriod = period_ends_at ?? null;
              const dismissalIsCurrent =
                dismissedForPeriod !== null && dismissedForPeriod === livePeriod;
              // A never-subscribed account sits at 0 credits because it hasn't
              // started a trial — that's the pre-trial state, not exhaustion.
              // Don't ambush a brand-new signup with the "out of credits" modal;
              // they belong in the start-a-trial funnel (the pricing page). Only
              // suppress when Rails explicitly says false — pages that omit the
              // flag (undefined) keep the old behavior.
              const neverSubscribed = has_ever_subscribed === false;
              if (!dismissalIsCurrent && !neverSubscribed) {
                updates.showOutOfCreditsModal = true;
                if (dismissedForPeriod !== null) {
                  // Period has rolled over — clear the stale dismissal so the
                  // user can dismiss the new period's modal independently.
                  updates.oocModalDismissedForPeriod = null;
                }
              }
            }
          }
        }
        if (plan_credits !== undefined && plan_credits !== null) {
          updates.planCredits = plan_credits;
        }
        if (pack_credits !== undefined && pack_credits !== null) {
          updates.packCredits = pack_credits;
        }
        if (plan_credits_allocated !== undefined && plan_credits_allocated !== null) {
          updates.planCreditsAllocated = plan_credits_allocated;
        }
        if (period_ends_at !== undefined) {
          updates.periodEndsAt = period_ends_at ?? null;
        }
        if (is_trialing !== undefined && is_trialing !== null) {
          updates.isTrialing = is_trialing;
        }
        if (trial_ends_at !== undefined) {
          updates.trialEndsAt = trial_ends_at ?? null;
        }
        if (trial_upgrade !== undefined) {
          updates.trialUpgrade = trial_upgrade
            ? {
                subscriptionPrefixId: trial_upgrade.subscription_prefix_id,
                planDisplayName: trial_upgrade.plan_display_name,
                amountCents: trial_upgrade.amount_cents,
                currency: trial_upgrade.currency,
                interval: trial_upgrade.interval,
                paymentFailed: trial_upgrade.payment_failed ?? false,
                declineMessage: trial_upgrade.decline_message ?? null,
                updatePaymentMethodPath: trial_upgrade.update_payment_method_path ?? null,
              }
            : null;
        }

        if (Object.keys(updates).length > 0) {
          set(updates);
        }

        // Check if low credit modal should be triggered.
        // This runs after setting updates so we use fresh + existing state.
        const state = get();
        const effectiveAllocated = updates.planCreditsAllocated ?? state.planCreditsAllocated;
        const effectivePlanCredits = updates.planCredits ?? state.planCredits;
        const effectiveOutOfCredits = updates.isOutOfCredits ?? state.isOutOfCredits;

        if (
          !effectiveOutOfCredits &&
          !state.showLowCreditModal &&
          effectiveAllocated !== null &&
          effectiveAllocated > 0 &&
          effectivePlanCredits !== null
        ) {
          const used = effectiveAllocated - effectivePlanCredits;
          const usagePercent = Math.round((used / effectiveAllocated) * 100);

          if (usagePercent >= LOW_CREDIT_WARNING_THRESHOLD_PERCENT) {
            const now = Date.now();
            const canShow =
              !state.lowCreditWarningDismissedAt ||
              now - state.lowCreditWarningDismissedAt > LOW_CREDIT_WARNING_SUPPRESS_DURATION_MS;

            if (canShow) {
              set({ showLowCreditModal: true });
            }
          }
        }
      },

      dismissModal: () => {
        // Bind the dismissal to the current billing period. If periodEndsAt
        // is null (no subscription / unknown period), use a synthetic sentinel
        // so re-arm only happens explicitly via credit restoration. The next
        // hydration with a real period will re-pop because the sentinel
        // string won't match.
        const periodEndsAt = get().periodEndsAt ?? "__unknown_period__";
        set({
          showOutOfCreditsModal: false,
          oocModalDismissedForPeriod: periodEndsAt,
        });
      },

      showModal: () => {
        // Manual trigger (e.g., chat input lock when the user tries to act
        // while OOC). Always show — the user just attempted an action that
        // requires credits, so they need the explanation.
        set({ showOutOfCreditsModal: true });
      },

      dismissLowCreditWarning: () => {
        set({
          showLowCreditModal: false,
          lowCreditWarningDismissedAt: Date.now(),
        });
      },

      reset: () => set(initialState),
    })),
    {
      name: "credit-store",
      partialize: (state) => ({
        exhaustedAt: state.exhaustedAt,
        oocModalDismissedForPeriod: state.oocModalDismissedForPeriod,
        lowCreditWarningDismissedAt: state.lowCreditWarningDismissedAt,
      }),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectBalance = (s: CreditStore) => s.balance;
export const selectIsOutOfCredits = (s: CreditStore) => s.isOutOfCredits;
export const selectShowOutOfCreditsModal = (s: CreditStore) => s.showOutOfCreditsModal;

// ============================================================================
// Convenience hooks
// ============================================================================

export function useIsOutOfCredits() {
  return useCreditStore(selectIsOutOfCredits);
}

export function useShowOutOfCreditsModal() {
  return useCreditStore(selectShowOutOfCreditsModal);
}

export function useCreditBalance() {
  return useCreditStore((s) => ({
    balance: s.balance,
    plan: s.planCredits,
    pack: s.packCredits,
    allocated: s.planCreditsAllocated,
  }));
}

/**
 * Calculate usage percentage (0-100).
 * Returns null if data not available.
 */
export function useUsagePercent(): number | null {
  return useCreditStore((s) => {
    if (s.planCreditsAllocated === null || s.planCreditsAllocated === 0) return null;
    if (s.planCredits === null) return null;

    const used = s.planCreditsAllocated - s.planCredits;
    return Math.round((used / s.planCreditsAllocated) * 100);
  });
}

/**
 * Format credits as a human-readable string.
 * Input is credits (not millicredits).
 */
export function formatCredits(credits: number | null): string {
  if (credits === null) return "—";
  if (credits >= 1000) {
    return `${(credits / 1000).toFixed(1)}k`;
  }
  // For values >= 10, show no decimals
  if (Math.abs(credits) >= 10) {
    return credits.toFixed(0);
  }
  // For values < 10, show 1 decimal only if needed
  const rounded = Math.round(credits * 10) / 10;
  return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
}
